import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  TextField,
  styled,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "@/components/custom/custom-button";
import { useState } from "react";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(1),
    maxWidth: 460,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  flexShrink: 0,
  backgroundColor: theme.palette.error.main + "18",
}));

export function DisputeReasonDialog({
  open,
  onClose,
  title,
  description,
  reasonLabel = "Reason",
  reasonPlaceholder = "",
  confirmLabel = "Confirm",
  mutation,
  onConfirm,
}) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  const isPending = mutation?.isPending;
  const isError = mutation?.isError;
  const isSuccess = mutation?.isSuccess;

  if (isSuccess && open) {
    mutation?.reset?.();
    onClose();
  }

  const handleClose = () => {
    if (isPending) return;
    setReason("");
    setReasonError("");
    mutation?.reset?.();
    onClose();
  };

  const handleConfirm = () => {
    if (!reason.trim()) {
      setReasonError(`Please provide a ${reasonLabel.toLowerCase()}.`);
      return;
    }
    setReasonError("");
    onConfirm(reason.trim());
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ pt: 2, px: 2, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconWrapper>
            <ErrorOutlineIcon sx={{ fontSize: 22, color: "error.main" }} />
          </IconWrapper>

          <Box sx={{ flex: 1 }}>
            <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
              {title}
            </DialogTitle>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            disabled={isPending}
            sx={{ alignSelf: "flex-start", mt: -0.5, mr: -0.5, color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
            {reasonLabel} <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={reasonPlaceholder}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim()) setReasonError("");
            }}
            disabled={isPending}
            error={!!reasonError}
            helperText={reasonError}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" },
            }}
          />
        </Box>

        {isError && (
          <CustomErrorMessage
            message={mutation?.error?.message ?? "Something went wrong. Please try again."}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
        <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={isPending}>
          Cancel
        </CustomButton>

        <CustomButton
          variantType="destructive"
          size="small"
          onClick={handleConfirm}
          disabled={isPending}
          sx={{ minWidth: 120 }}
        >
          {isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              Please wait...
            </Box>
          ) : (
            confirmLabel
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
}
